import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

type Theme = 'light' | 'dark'

const STORAGE_KEY = 'theme'

// The device's own light/dark setting. Guarded: jsdom (and very old WebViews)
// have no matchMedia.
const darkQuery = typeof window !== 'undefined' ? window.matchMedia?.('(prefers-color-scheme: dark)') : undefined

function stored(): Theme | null {
  const value = localStorage.getItem(STORAGE_KEY)
  return value === 'light' || value === 'dark' ? value : null
}

function device(): Theme {
  return darkQuery?.matches ? 'dark' : 'light'
}

export const useThemeStore = defineStore('theme', () => {
  // A choice made in the app wins; until there is one, the device decides.
  // ⚠ The device's answer is never written back: persisting it on first load
  // would freeze today's OS setting into a « choice » nobody made.
  const currentTheme = ref<Theme>(stored() ?? device())

  darkQuery?.addEventListener?.('change', () => {
    if (!stored()) currentTheme.value = device()
  })

  const toggleTheme = () => {
    currentTheme.value = currentTheme.value === 'light' ? 'dark' : 'light'
    localStorage.setItem(STORAGE_KEY, currentTheme.value)
  }

  // Apply theme to document element whenever it changes
  watch(currentTheme, (newTheme) => {
    document.documentElement.classList.toggle('dark', newTheme === 'dark')
  }, { immediate: true })

  return {
    currentTheme,
    toggleTheme
  }
})
